import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArchiveOutlined as ArchiveIcon,
  MarkEmailUnreadOutlined as MarkUnreadIcon,
  PersonOffOutlined as BlockSenderIcon,
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  ReportGmailerrorredOutlined as SpamIcon,
} from '@mui/icons-material';
import type { FinanceEmailThreadMailboxHandlers } from './types';

type FinanceEmailThreadToolbarProps = {
  subject: string;
  clientName?: string | null;
  fromAddress?: string | null;
  messageCount: number;
  allHistoryIds: string[];
  inboundHistoryIds: string[];
  handledInboundIds: string[];
  latestInboundId: string | null;
  canReply: boolean;
  canReplyAll?: boolean;
  canSpam: boolean;
  canBlockSender: boolean;
  canMarkUnread: boolean;
  isReplying?: boolean;
  onReply: (historyId: string, replyAll?: boolean) => void;
  mailbox?: FinanceEmailThreadMailboxHandlers;
};

export const FinanceEmailThreadToolbar = ({
  subject,
  clientName,
  fromAddress,
  messageCount,
  allHistoryIds,
  inboundHistoryIds,
  handledInboundIds,
  latestInboundId,
  canReply,
  canReplyAll = false,
  canSpam,
  canBlockSender,
  canMarkUnread,
  isReplying,
  onReply,
  mailbox,
}: FinanceEmailThreadToolbarProps) => {
  const acting = Boolean(mailbox?.isActing);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        px: { xs: 1.5, md: 2.5 },
        pt: 1.25,
        pb: 1.25,
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'background.default' : 'grey.50'),
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
        {mailbox && (
          <>
            <Tooltip title="Archive">
              <span>
                <IconButton
                  size="small"
                  disabled={acting || allHistoryIds.length === 0}
                  onClick={() => mailbox.onArchive(allHistoryIds)}
                  aria-label="Archive"
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Report spam">
              <span>
                <IconButton
                  size="small"
                  disabled={acting || !canSpam}
                  onClick={() => mailbox.onSpam(inboundHistoryIds)}
                  aria-label="Report spam"
                  color="warning"
                >
                  <SpamIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Mark as unread">
              <span>
                <IconButton
                  size="small"
                  disabled={acting || !canMarkUnread}
                  onClick={() => mailbox.onMarkUnread(handledInboundIds)}
                  aria-label="Mark as unread"
                >
                  <MarkUnreadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={fromAddress ? `Block ${fromAddress}` : 'Block sender'}>
              <span>
                <IconButton
                  size="small"
                  disabled={acting || !canBlockSender || !latestInboundId}
                  onClick={() => {
                    if (latestInboundId) {
                      mailbox.onBlockSender(latestInboundId, fromAddress);
                    }
                  }}
                  aria-label="Block sender"
                >
                  <BlockSenderIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
          </>
        )}

        <Tooltip title="Reply">
          <span>
            <IconButton
              size="small"
              disabled={isReplying || !canReply}
              onClick={() => latestInboundId && onReply(latestInboundId, false)}
              aria-label="Reply"
            >
              <ReplyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canReplyAll && (
          <Tooltip title="Reply all">
            <span>
              <IconButton
                size="small"
                disabled={isReplying || !canReply}
                onClick={() => latestInboundId && onReply(latestInboundId, true)}
                aria-label="Reply all"
              >
                <ReplyAllIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {acting && <CircularProgress size={16} sx={{ mr: 1 }} />}
        <Typography variant="caption" color="text.secondary">
          {messageCount} {messageCount === 1 ? 'message' : 'messages'}
        </Typography>
      </Stack>

      <Typography
        variant="h5"
        sx={{
          fontSize: { xs: '1.15rem', md: '1.35rem' },
          fontWeight: 500,
          lineHeight: 1.3,
          pr: 1,
        }}
      >
        {subject || '(no subject)'}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.75 }}
      >
        {clientName && <Chip size="small" label={clientName} sx={{ height: 24 }} />}
        {fromAddress && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {fromAddress}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
